import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { nestedNodeDelimiter, } from "@buildwithflux/models";
import { devAssert } from "../../../../../../helpers";
import { getPropertyValueRobust } from "../../../../../../property";
/** Note re the intention behind how we match pads to terminals:
 *
 * What we want at a high level is
 * 1. Pads from kicad footprint assets should match with the correct Flux genesis terminal
 *    (kicadPad.name === fluxTerminal.pin_number property).
 *    (*) See note re terminalId below
 * 2. Mounting pads without terminals should be detected as such (by returning undefined below)
 *    so that kicad mounting pads are placed correctly into the "Mounts" folder.
 * 3. To allow users to add their own pads and have them associated with kicad pads.
 *    (user sets padNode.name === kicadPad.name)
 * 4. To have all this also work with submodules (i.e. terminals in an arbitrarily-nested submodule
 *    should be correctly associated with the submodule's footprint asset's terminal)
 * 5. Bonus points for enabling many to many relationships (eg multiple pads belonging to a single terminal?)
 *    Our uid-matching scheme doesn't support this. Kicad and other tools do. Future feature.
 *
 * (*) Note: padNode.terminalId should be equal to fluxTerminal.pin_number property, but we've
 * had bugs where they don't stay in sync, so take care. We're not certain whether there's
 * data out there with extracted elements that don't include the pin_number property --
 * if we could be sure it always existed, we should be able to eliminate the .terminalId field.
 */
export function getMatchingPadNode(footprintPadName, existingPadNodes, elements, isInSubmodule, logger) {
    return existingPadNodes.find((padNode) => {
        // Note: `padNode.terminalId` is better named `.pinNumber`; it's the pin number
        // from the terminal that was used to create padNode originally, in
        // setElementsAndGenerateNodes->setElement()
        const existingPadTerminalId = padNode.terminalId?.toString().trim();
        const existingPadName = padNode.name?.toString().trim();
        const genesisTerminalPinNumber = isInSubmodule
            ? getPinNumberFromTerminalElementInSubmodule(padNode.uid, elements, logger)
            : getPinNumberFromTerminalElement(padNode.uid, elements);
        return (existingPadTerminalId === footprintPadName ||
            existingPadName === footprintPadName ||
            genesisTerminalPinNumber === footprintPadName);
    });
}
function getPinNumberFromTerminalElement(padNodeUid, elements) {
    const elementUid = padNodeUid.split(nestedNodeDelimiter)[1] || padNodeUid;
    if (!elements[elementUid]) {
        return undefined;
    }
    const prop = Object.values(elements[elementUid].properties || {}).find((p) => p.name === defaultPropertyDefinitions.pin_number.label);
    return prop?.value;
}
function getPinNumberFromTerminalElementInSubmodule(padNodeUid, elements, logger) {
    // Recurse into nested elements (splitting uid by the delimiter) to get the
    // pin number property of the element matching the padNodeUid.
    const uids = padNodeUid.split(nestedNodeDelimiter);
    for (const uid of uids) {
        const elms = elements[uid]?.part_version_data_cache?.extracted_element_cache;
        if (!elms) {
            // TODO feels wrong that the terminating case is when there are no sub-elements -- the loop on `uids`
            // should have exactly the right depth and we should run the loop to completion. If the nesting
            // depth doesn't match uids.length, then that's corrupt data and we should handle separately.
            // But I don't want to risk breaking this, so leaving as-is.
            const element = elements[uid];
            if (!element || !element.properties)
                return undefined;
            return getPropertyValueRobust(defaultPropertyDefinitions.pin_number, element.properties);
        }
        else {
            elements = elms;
        }
    }
    devAssert(false, "Pad node uid points to an element with sub-elements. This is probably valid and means the loop in this function should be refactored. See TODO.", logger);
    return undefined;
}
//# sourceMappingURL=getMatchingPadNode.js.map